import { useQuery } from '@tanstack/react-query';
import { axios } from '@/lib/axios/axios';
import { QueryConfig } from '@/lib/react-query/react-query';
import { MESSAGING_API_BASE, messagingQueryKeys } from '../config';
import {
  EligibleUsersResponse,
  ChannelTypeEnum,
  UserInfo,
} from '../types/backend';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/types';
import { isExternalUser } from '../../helpers/userAuthorization';

export type EligibleUsersForCreationQuery = {
  channelType:
    | ChannelTypeEnum.INTERNAL
    | ChannelTypeEnum.CLIENT
    | ChannelTypeEnum.DM;
  projectId?: number;
  search?: string;
  limit?: number;
  offset?: number;
};

const getEligibleUsersForCreation = async (
  params: EligibleUsersForCreationQuery
): Promise<EligibleUsersResponse> => {
  const { channelType, projectId, search, limit = 100, offset = 0 } = params;

  const queryParams = new URLSearchParams({
    'channel-type': channelType,
    limit: String(limit),
    offset: String(offset),
  });

  if (projectId) {
    queryParams.append('project-id', String(projectId));
  }

  // Always include the `search` param when provided by the caller.
  // If `search` is only whitespace, send an empty param (search=) instead of
  // a '+' which is the URL-encoded representation of space. Backend can
  // interpret empty `search=` as a wildcard (return all results).
  if (search !== undefined) {
    const value = /^\s*$/.test(search) ? '' : search;
    queryParams.append('search', value);
  }

  return axios.get(
    `${MESSAGING_API_BASE}/channels/eligible-users?${queryParams}`
  );
};

/**
 * Fetch all eligible users for channel creation by paginating through all available records
 * until the total count is reached
 */
const getAllEligibleUsersForCreation = async (
  params: EligibleUsersForCreationQuery
): Promise<EligibleUsersResponse> => {
  const { channelType, projectId, search, limit = 100 } = params;

  let allUsers: UserInfo[] = [];
  let offset = 0;
  let totalCount = 0;

  // Keep fetching until we have all users
  while (true) {
    const response = await getEligibleUsersForCreation({
      channelType,
      projectId,
      search,
      limit,
      offset,
    });

    allUsers = [...allUsers, ...response.rows];
    totalCount = response.count;

    // Break if we've fetched all users
    if (allUsers.length >= totalCount) {
      break;
    }

    // Move to next page
    offset += limit;
  }

  return {
    rows: allUsers,
    count: totalCount,
  };
};

type QueryFnType = typeof getAllEligibleUsersForCreation;

type UseEligibleUsersForCreationOptions = {
  channelType:
    | ChannelTypeEnum.INTERNAL
    | ChannelTypeEnum.CLIENT
    | ChannelTypeEnum.DM;
  projectId?: number;
  search?: string;
  limit?: number;
  fetchAll?: boolean; // If true, fetch all records; if false, fetch single page
  offset?: number;
  config?: QueryConfig<QueryFnType>;
};

export const useEligibleUsersForCreation = ({
  channelType,
  projectId,
  search,
  limit,
  fetchAll = true,
  offset,
  config,
}: UseEligibleUsersForCreationOptions) => {
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const isExternal = loggedUser ? isExternalUser(loggedUser) : false;

  return useQuery({
    queryKey: [
      ...messagingQueryKeys.eligibleUsersForCreation(
        channelType,
        projectId,
        search
      ),
      { fetchAll, limit, offset },
    ],
    queryFn: () =>
      fetchAll
        ? getAllEligibleUsersForCreation({
            channelType,
            projectId,
            search,
            limit,
          })
        : getEligibleUsersForCreation({
            channelType,
            projectId,
            search,
            limit,
            offset,
          }),
    staleTime: 30000, // Cache for 30 seconds
    enabled: !isExternal && config?.enabled !== false,
    ...config,
  });
};
